import type { AdminCase } from "../../lib/moderation-api";
import { StatusBadge, fmtDate, shortId } from "./adminShared";
import { cn } from "../cn";

/**
 * Left column — the moderation case ledger. Rows are dense, mono, HUD-styled.
 * Selection drives the detail column. No mutation happens here.
 */
export function AdminCaseList({
  cases,
  loading,
  selectedId,
  onSelect,
}: {
  cases: AdminCase[];
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div data-testid="admin-case-list" className="panel-chamfer panel-chamfer--sm overflow-hidden">
      <div className="chamfer-strip">
        <span className="strip-dot" aria-hidden />
        <span>Cases</span>
        <span className="ml-auto font-mono text-[9px] tracking-[0.14em] text-text-muted">
          {loading ? "…" : cases.length}
        </span>
      </div>
      <div className="max-h-[62vh] overflow-y-auto scroll-dark">
        {loading && (
          <div data-testid="admin-case-list-loading" className="px-4 py-6 text-center font-mono text-[11px] text-text-muted">
            Loading cases…
          </div>
        )}
        {!loading && cases.length === 0 && (
          <div data-testid="admin-case-list-empty" className="px-4 py-8 text-center font-mono text-[11px] text-text-muted">
            No cases match this view.
          </div>
        )}
        <ul className="divide-y divide-white/5">
          {cases.map((c) => (
            <li key={c.case_id}>
              <button
                type="button"
                data-testid={`admin-case-row-${c.case_id}`}
                data-selected={selectedId === c.case_id}
                onClick={() => onSelect(c.case_id)}
                className={cn(
                  "w-full text-left px-4 py-3 transition-colors",
                  selectedId === c.case_id ? "bg-[color:var(--spec-cyan-soft)]" : "hover:bg-white/[0.03]",
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-text-muted">
                    {c.target_type}
                  </span>
                  <StatusBadge status={c.status} />
                  {(c.support_count ?? 0) > 0 && (
                    <span
                      data-testid={`admin-case-support-badge-${c.case_id}`}
                      data-awaiting={c.awaiting_reply ? "true" : "false"}
                      data-unread={c.admin_unread ? "true" : "false"}
                      title={
                        c.admin_unread
                          ? "Unread owner message — open to read"
                          : c.awaiting_reply
                          ? "Owner is awaiting your reply"
                          : "Support messages in this case"
                      }
                      className={cn(
                        "inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-[0.12em]",
                        c.awaiting_reply
                          ? "text-neon-coral border-[color:var(--neon-coral-ring)] bg-[color:var(--neon-coral-soft)]"
                          : "text-neon-teal border-border-accent",
                        c.admin_unread && "badge-unread-pulse",
                      )}
                    >
                      <svg width="9" height="9" viewBox="0 0 14 14" fill="none" aria-hidden>
                        <path d="M2 3.5h10v6H6l-3 2.5v-2.5H2z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                      </svg>
                      {c.support_count}
                      {c.awaiting_reply && <span className="ml-0.5">· reply</span>}
                    </span>
                  )}
                  <span className="ml-auto font-mono text-[9px] text-text-muted">{fmtDate(c.updated_at)}</span>
                </div>
                <div className="mt-1.5 font-mono text-[11px] text-text-primary break-all">
                  {shortId(c.target_id, 12, 8)}
                </div>
                <div className="mt-0.5 text-[10.5px] text-text-secondary truncate">
                  {c.reason_label ?? c.reason_category}
                  {c.emergency && <span className="ml-2 text-neon-coral">· emergency</span>}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
